import SignaturePad from 'react-signature-canvas';
import { Link, useHistory } from 'react-router-dom';
import { MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { omit } from 'lodash';

import GlobalContextI from '../interfaces/GlobalContext';
import GlobalContext from '../contexts/GlobalContext';
import GuestI from '../interfaces/Guest';

import getGuestID from '../functions/getGuestID';
import { parseDob, ageFrom, fmtMdy } from '../functions/dob';
import getGuestByGuestID from '../functions/getGuestByGuestID';
import { businessDayStart } from '../functions/businessDay';
import useFee from '../hooks/useFee';

import Button from './Button';
import FlexContainer from './FlexContainer';

const MIN_ENTRY_AGE = 18;

const ScanNewGuestTos = () => {
    const { guest, setGuest } = useContext<GlobalContextI>(GlobalContext);
    const history = useHistory();
    const [agreed, setAgreed] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [hasInk, setHasInk] = useState<boolean>(false);
    const submittingRef = useRef<boolean>(false);
    const sigPadRef = useRef<any>(null);
    // Switches the "…" placeholder over to "Calculating price…" once
    // fee fetch passes a 500ms threshold. Tells staff the system isn't
    // hung when the network is briefly slow.
    const [feeStuck, setFeeStuck] = useState<boolean>(false);
    const [flashName, setFlashName] = useState<string | null>(null);

    // Fee preview — same logic the existing flow uses (NoticeGuestPage
    // and UpdateGuestForm both call useFee). For Limited we fall through
    // to /api/fees/:day which honors the EventFee table for the current
    // business-day-of-week (Sat 1am still hits Friday's tier). Discount
    // rebate is applied if the guest carries a discount field. For
    // brand-new scanned guests there is no discount so the base fee is
    // returned. This is the SAME function used for existing-guest
    // confirmations — no parallel fee math.
    const feeType =
        guest?.type === 'allAccess' ? 'AAEventFee' : 'EventFee';
    const feeDay = businessDayStart().getDay();
    const feeToday = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
    });
    const { fee, loadingFee } = useFee(
        feeType,
        (guest || { points: 0, guestID: '', discount: undefined }) as any,
        feeDay,
        feeToday
    );

    useEffect(() => {
        if (!loadingFee) {
            setFeeStuck(false);
            return;
        }
        const t = window.setTimeout(() => setFeeStuck(true), 500);
        return () => window.clearTimeout(t);
    }, [loadingFee]);

    if (!guest) {
        history.replace('/kiosk');
        return null;
    }

    const type: string = (guest.type as string) || 'limited';
    const dob = parseDob(guest.dateOfBirth);
    const age = ageFrom(dob);
    const fullName = [guest.firstName, guest.lastName].filter(Boolean).join(' ');
    const dobDisplay = fmtMdy(dob);

    const isUnderage = age != null && age < MIN_ENTRY_AGE;

    // Big top-of-page decision banner. The four states are mutually
    // exclusive and chosen so staff can decide at a glance without
    // reading the info card.
    type Decision = {
        label: string;
        sub?: string;
        bg: string;
        border: string;
    };
    const decision: Decision = (() => {
        if (age == null) {
            return {
                label: 'DOB UNREADABLE — RESCAN ID',
                bg: '#7f8c8d',
                border: '#34495e',
            };
        }
        if (age < 18) {
            return {
                label: '⛔ DO NOT ADMIT',
                sub: 'Guest is underage.',
                bg: '#c0392b',
                border: '#7b1d12',
            };
        }
        if (age < 21) {
            return {
                label: '⚠️ 18+ BUT UNDER 21',
                sub: 'Do not allow alcohol.',
                bg: '#e67e22',
                border: '#a14d0c',
            };
        }
        return {
            label: '✅ OK TO ADMIT',
            bg: '#27ae60',
            border: '#0f6d38',
        };
    })();
    const ageColor = (() => {
        if (age == null) return '#666';
        if (age < 18) return '#c0392b';
        if (age < 21) return '#e67e22';
        return '#27ae60';
    })();

    const cancel = () => history.push(`/guests/checkin/${type}`);

    const runCheckIn = async (
        createdGuestID: string,
        createdId: string,
        feeValue: number
    ) => {
        if (type === 'allAccess') {
            // PinkFeeService is the server-side single source of truth
            // for All Access fees + atomic check-in. It computes its
            // own value — we don't pass the previewed fee in.
            await axios.post('/api/pink-fee/checkin', {
                guestID: createdGuestID,
                paymentMethod: 'cash',
            });
            return;
        }
        // Limited: legacy two-step. Use the fee already previewed by
        // useFee so the displayed AMOUNT DUE matches what gets charged.
        await axios.post('/api/transactions', {
            guestID: createdGuestID,
            firstName: guest.firstName,
            lastName: guest.lastName,
            stageName: 'NONE',
            fee: 'EventFee',
            type: 'limited',
            value: feeValue,
            points: 0,
        });
        await axios.patch(`/api/guests/${createdId}?type=limited`, {
            lifeTimeFees: feeValue,
            entryDates: [new Date()],
        });
    };

    const handleAccept = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        // Re-entry guard — prevents touch double-fire / double-click from
        // posting the create twice. State alone races: setLoading(true) is
        // queued and the button stays clickable until React re-renders.
        // A ref flips synchronously.
        if (submittingRef.current) return;
        setError('');

        if (age == null) {
            return setError('Could not read date of birth from scan. Please re-scan.');
        }
        if (isUnderage) {
            return setError('Guest is under the legal entry age — cannot check in.');
        }
        if (!agreed) {
            return setError('Guest must agree to the terms to continue.');
        }
        const pad = sigPadRef.current;
        if (!pad) {
            return setError('Signature pad is not ready. Please try again.');
        }
        if (pad.isEmpty?.()) {
            return setError('Signature is required.');
        }

        submittingRef.current = true;
        setLoading(true);
        try {
            const newGuestID = getGuestID({
                firstName: guest.firstName,
                lastName: guest.lastName,
                licenseID: guest.licenseID,
            }).toLowerCase();

            // Strip server-managed fields defensively. Anything carrying
            // an _id from a prior context-guest would otherwise blow up
            // with E11000 dup-_id when Mongoose tries to honor it.
            // AllAccessGuest schema marks stageName as required; the
            // scan path has no entertainer name to surface, so we
            // default to the legal name + guestID. Staff can edit it
            // later from the dashboard. For Limited the field is
            // optional and just gets stored as a default.
            const stageNameDefault =
                ([guest.firstName, guest.lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                    newGuestID) +
                (type === 'allAccess' ? ` (${newGuestID})` : '');
            const seed: GuestI = {
                ...(guest as GuestI),
                guestID: newGuestID,
                type,
                stageName:
                    (guest as any).stageName?.trim?.() || stageNameDefault,
                tos: true,
                lease: true,
                entryDates: [],
            };
            const empties = Object.keys(seed).filter(
                (k) => (seed as any)[k] === ''
            );
            const payload = omit(seed, [
                ...empties,
                '_id',
                '__v',
                'createdAt',
                'updatedAt',
            ]);

            // 1. Create the guest. If we collide on guestID (because a
            //    prior accept already wrote this exact one), fall through
            //    to the existing-guest path: fetch + use the saved doc.
            let created: any;
            try {
                const res = await axios.post(
                    `/api/guests/${type}/create`,
                    payload
                );
                created = res.data;
            } catch (err: any) {
                // ANY create failure triggers a lookup. The server
                // returns a generic 500 ("Internal server error") for
                // Mongo dup-key errors, so we cannot reliably regex
                // the message — fall through to lookup unconditionally.
                // If the guest is in the DB (a prior request raced or
                // got throttled and the duplicate is the loser), we
                // happily reuse the existing record. Otherwise rethrow.
                const existing = await getGuestByGuestID(
                    newGuestID,
                    type
                ).catch(() => null);
                if (existing && (existing as any)._id) {
                    created = existing;
                } else {
                    throw err;
                }
            }
            await setGuest(created);

            // 2. Save signature.
            const signatureDataUrl = pad
                .getTrimmedCanvas()
                .toDataURL('image/png');
            const base64 = signatureDataUrl.split(',')[1];
            await axios.post('/api/signatures/create', {
                signature: base64,
                guestID: created.guestID,
                type,
            });

            // 3. Run check-in. Pass the previewed fee so the
            //    transaction matches what the page displayed.
            await runCheckIn(
                created.guestID,
                created._id,
                fee?.value ?? 0
            );

            // 4. Success flash for 500ms, then back to scan input.
            //    Only fires after every API call above succeeded — any
            //    failure throws and lands in the catch below, which
            //    surfaces the error and skips the flash.
            const fullName = [created.firstName, created.lastName]
                .filter(Boolean)
                .join(' ');
            setFlashName(fullName || created.guestID || 'Guest');
            // Always return to the scanner page for this guest type.
            // Scan-new is unconditionally part of the scan flow, so the
            // staff/kiosk session origin doesn't matter — they need the
            // empty, focused scan input next, not /kiosk.
            const dest = `/guests/checkin/${type}`;
            window.setTimeout(() => {
                // Clear context so the next scan starts from a clean
                // slate — no stale _id / guestID lingering across
                // back-to-back kiosk check-ins.
                setGuest(null as any);
                history.push(dest);
            }, 500);
            return;
        } catch (err: any) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Check-in failed. Try again or check the network.'
            );
            submittingRef.current = false;
        } finally {
            setLoading(false);
        }
    };

    // Keep the form mounted during submission so the disabled-reason
    // hint stays visible. The button stays disabled and the user sees
    // "Processing…" rather than a full-page spinner replacing the form.
    const feeReady = !loadingFee && fee != null;
    const acceptDisabled =
        loading ||
        isUnderage ||
        age == null ||
        !agreed ||
        !hasInk ||
        !feeReady;
    const disabledReason: string | null = (() => {
        if (loading) return 'Processing…';
        if (isUnderage) return 'Guest underage';
        if (age == null) return 'Date of birth unreadable — re-scan';
        if (!feeReady) return 'Loading fee…';
        if (!agreed) return 'Agreement required';
        if (!hasInk) return 'Signature required';
        return null;
    })();

    // Inline styles for a kiosk-comfortable layout that fits one screen.
    // Keeping it inline avoids touching the global stylesheet.
    const containerStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
    };
    const sigBoxStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 420,
        margin: '8px auto 4px',
    };
    const buttonRowStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        width: '100%',
        maxWidth: 320,
        margin: '12px auto 0',
    };
    const buttonWrapStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 280,
    };

    // Full-screen success flash. Rendered only after every check-in
    // step succeeded; the redirect setTimeout fires 500ms later.
    if (flashName) {
        return (
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#27ae60',
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: 24,
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        fontSize: 56,
                        fontWeight: 900,
                        letterSpacing: 2,
                        marginBottom: 16,
                    }}
                >
                    ✅ CHECK-IN COMPLETE
                </div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                    Guest: {flashName}
                </div>
            </div>
        );
    }

    return (
        <FlexContainer>
            <div className="agree" style={containerStyle}>
                <div
                    style={{
                        background: decision.bg,
                        color: '#fff',
                        padding: '22px 18px',
                        borderRadius: 14,
                        fontSize: 32,
                        fontWeight: 900,
                        textAlign: 'center',
                        marginBottom: 14,
                        border: `4px solid ${decision.border}`,
                        letterSpacing: 1.5,
                        lineHeight: 1.15,
                    }}
                >
                    <div>{decision.label}</div>
                    {decision.sub && (
                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 16,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                opacity: 0.95,
                            }}
                        >
                            {decision.sub}
                        </div>
                    )}
                </div>
                <div
                    className="notice-guest-container__text"
                    style={{
                        background: '#1f1f1f',
                        border: '2px solid #444',
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 12,
                    }}
                >
                    <p style={{ fontSize: 18, margin: '2px 0' }}>
                        <strong>Guest:</strong>{' '}
                        <span style={{ color: '#fff' }}>
                            {fullName || '(unknown)'}
                        </span>
                    </p>
                    <p style={{ fontSize: 18, margin: '2px 0' }}>
                        <strong>DOB:</strong>{' '}
                        <span style={{ color: '#fff' }}>
                            {dobDisplay || '(unknown)'}
                        </span>
                    </p>
                    <div
                        style={{
                            marginTop: 10,
                            padding: '10px 16px',
                            background: ageColor,
                            color: '#fff',
                            borderRadius: 8,
                            fontSize: 28,
                            fontWeight: 800,
                            letterSpacing: 1,
                            textAlign: 'center',
                        }}
                    >
                        AGE: {age == null ? '?' : age}
                    </div>
                    <div
                        style={{
                            marginTop: 8,
                            padding: '10px 16px',
                            background: '#2c3e50',
                            color: '#fff',
                            borderRadius: 8,
                            fontSize: 26,
                            fontWeight: 800,
                            letterSpacing: 1,
                            textAlign: 'center',
                        }}
                    >
                        AMOUNT DUE:{' '}
                        {loadingFee || fee == null
                            ? feeStuck
                                ? 'Calculating price…'
                                : '…'
                            : `$${fee.value}`}
                    </div>
                </div>

                <div
                    style={{
                        fontSize: 13,
                        lineHeight: 1.4,
                        margin: '4px 0 8px',
                    }}
                >
                    <input
                        type="checkbox"
                        defaultChecked={agreed}
                        onClick={(e) => setAgreed(e.currentTarget.checked)}
                        style={{ marginRight: 6, verticalAlign: 'middle' }}
                    />
                    <span>
                        Guest agrees to the{' '}
                        <Link to="/tos" target="_blank">
                            terms
                        </Link>{' '}
                        and{' '}
                        <Link to="/lease" target="_blank">
                            lease agreement
                        </Link>
                        .
                    </span>
                </div>

                <div className="sig-container" style={sigBoxStyle}>
                    <div
                        style={{
                            fontSize: 13,
                            color: '#aaa',
                            marginBottom: 4,
                        }}
                    >
                        Sign here
                    </div>
                    <div
                        style={{
                            border: '1px solid #555',
                            borderRadius: 6,
                            background: '#fff',
                        }}
                    >
                        <SignaturePad
                            canvasProps={{
                                className: 'sig-pad',
                                width: 420,
                                height: 150,
                                style: {
                                    width: '100%',
                                    height: 150,
                                    display: 'block',
                                },
                            }}
                            ref={sigPadRef}
                            onEnd={() => {
                                const pad = sigPadRef.current;
                                setHasInk(!!pad && !pad.isEmpty());
                            }}
                        />
                    </div>
                </div>

                {error !== '' && (
                    <div
                        style={{
                            background: '#c0392b',
                            color: '#fff',
                            padding: 10,
                            borderRadius: 6,
                            margin: '10px auto 0',
                            maxWidth: 420,
                            fontWeight: 700,
                            textAlign: 'center',
                            fontSize: 14,
                        }}
                    >
                        <p style={{ margin: 0 }}>{error}</p>
                    </div>
                )}

                <div style={buttonRowStyle}>
                    <div style={buttonWrapStyle}>
                        <Button
                            color={'green'}
                            margin={false}
                            disabled={acceptDisabled}
                            onClick={handleAccept}
                        >
                            Accept &amp; Check In
                        </Button>
                        {disabledReason && (
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 13,
                                    fontStyle: 'italic',
                                    color: loading ? '#bbb' : '#e67e22',
                                    textAlign: 'center',
                                }}
                            >
                                {disabledReason}
                            </div>
                        )}
                    </div>
                    <div style={buttonWrapStyle}>
                        <Button
                            color={'red'}
                            margin={false}
                            onClick={(e: any) => {
                                if (e?.preventDefault) e.preventDefault();
                                const pad = sigPadRef.current;
                                if (pad) pad.clear();
                                setHasInk(false);
                            }}
                        >
                            Clear Signature
                        </Button>
                    </div>
                    <div style={buttonWrapStyle}>
                        <Button color={type} margin={false} onClick={cancel}>
                            Cancel / Back
                        </Button>
                    </div>
                </div>
            </div>
        </FlexContainer>
    );
};

export default ScanNewGuestTos;
